'use client';
/**
 * SPM 终端解析、查表与规则化编码工具。
 *
 * 全局框架组件（Header/Footer/侧边导航/面包屑）渲染在所有页面，需求方拍板其
 * B 位固定为 global_pc / global_wap（App WebView 内嵌时为 global_app），与所在
 * 页面无关：用 useGlobalSpmTerminal 按终端取表，再用 getSpmPositionCode 查
 * 「模块 C + 坑位 D」。查无页面/模块/坑位时返回 undefined，调用方不附加编码，
 * 天然兼容表外项。
 *
 * 例外：顶部导航、底部导航、侧边导航的位置坑位（nav_item_XX、submenu_item_GG_II、
 * footer_link_XX、footer_group_toggle_XX 等）按坑位序号规则出码
 * （buildSpmPositionCode），不受表快照约束——表是生产基线的核验参照，CMS 内容
 * 增删条目时序号照规则连续编码；固定语义坑位（logo_entry、footer_help 等）仍查表。
 *
 * 页面楼层组件（首页/列表/商详楼层，B 位跟随所在页面）仍走
 * useSpmPageTerminal：先按「当前路由 + 终端」解析页面终端，再查表。
 *
 * 终端归属（pc / wap / app）统一由 reporting/spm/terminal.ts 判定：App WebView
 * 内嵌时 B 位上报 <页面>_app 而非 <页面>_wap。
 */
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { getSpmPageCodes, type SpmPageId } from '@/reporting/spm';
import { useSpmTerminalKind } from '@/reporting/spm/spm-dom';
import { getSpmSitePrefix } from '@/reporting/spm/site';
import { resolveSpmTerminalKind } from '@/reporting/spm/terminal';
import { EPlatform } from '@/utils/enums/platform-enum';

/**
 * P0 路由 → 页面终端映射；其余路由返回 null（一切照旧，不附加编码）。
 * 纯函数版供测试与非 hook 场景使用；渲染期请用 useSpmPageTerminal（App 内嵌
 * 判定走 Provider 下发的服务端终端段，SSR 与客户端同值）。
 */
export function resolveSpmPageTerminal(
  pathname: string | null | undefined,
  deviceType: EPlatform,
): SpmPageId | null {
  if (!pathname) return null;
  if (pathname === '/') {
    return `home_${resolveSpmTerminalKind(deviceType)}`;
  }
  if (pathname.startsWith('/goods-detail/')) {
    return `product_detail_${resolveSpmTerminalKind(deviceType)}`;
  }
  return null;
}

/** 当前路由 + 终端对应的页面终端；非 P0 页面为 null。 */
export function useSpmPageTerminal(deviceType: EPlatform): SpmPageId | null {
  const { pathname } = useRouteMetadata();
  const kind = useSpmTerminalKind(deviceType);
  if (!pathname) return null;
  if (pathname === '/') return `home_${kind}`;
  if (pathname.startsWith('/goods-detail/')) return `product_detail_${kind}`;
  return null;
}

/**
 * 查当前页面终端编码表中「模块 C + 坑位 D」的完整编码。
 * 查无页面/模块/坑位均返回 undefined，由调用方决定不附加（表外）。
 */
export function getSpmPositionCode(
  page: SpmPageId | null,
  moduleCode: string,
  position: string,
): string | undefined {
  if (!page) return undefined;
  return getSpmPageCodes(page)[moduleCode]?.[position];
}

/**
 * 按坑位序号规则生成完整编码（A.B.C.D）：导航类位置坑位（nav_item_XX、
 * submenu_item_GG_II、footer_link_XX 等）按渲染序号推导，表外坑位照常出码，
 * 保证 CMS 条目增删后编码仍随位置连续。仅用于导航类位置坑位；固定语义坑位
 * 用 getSpmPositionCode 查表（保留表缺失即不上报的口径）。
 */
export function buildSpmPositionCode(
  page: SpmPageId,
  moduleCode: string,
  position: string,
): string {
  return `${getSpmSitePrefix()}.${page}.${moduleCode}.${position}`;
}

export function resolveGlobalSpmTerminal(
  deviceType: EPlatform,
): 'global_pc' | 'global_wap' | 'global_app' {
  return `global_${resolveSpmTerminalKind(deviceType)}`;
}

/**
 * 全局框架组件固定使用的全局终端（global_pc / global_wap / global_app），与所在页面无关。
 * App 内嵌判定优先走 Provider 下发的服务端终端段（SSR 与客户端同值），UA 兜底。
 */
export function useGlobalSpmTerminal(
  deviceType: EPlatform,
): 'global_pc' | 'global_wap' | 'global_app' {
  return `global_${useSpmTerminalKind(deviceType)}`;
}

/** D 位坑位序号统一两位数字（01、02…），与编码表命名一致。 */
export function padSpmPositionIndex(index: number): string {
  return String(index).padStart(2, '0');
}
